import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, Copy } from "lucide-react";
import api from "@/lib/api";

export default function SignupSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.signupStatus(sessionId);
        setStatus(res);
        if (res.status === "active" || res.status === "failed") {
          setPolling(false);
        } else if (attempts >= 30) {
          setPolling(false);
        }
      } catch {
        if (attempts >= 10) setPolling(false);
      }
      attempts++;
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const copyNumber = () => {
    if (status?.proxy_number) {
      navigator.clipboard.writeText(status.proxy_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        {polling ? (
          <div data-testid="signup-processing">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl tracking-tight mb-2">
              Activating your PROX number...
            </h1>
            <p className="font-mono text-xs text-zinc-500">
              This usually takes a few seconds.
            </p>
          </div>
        ) : status?.status === "active" ? (
          <div data-testid="signup-complete">
            <div className="w-12 h-12 bg-emerald-400/10 border border-emerald-400/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <h1 className="font-heading text-3xl tracking-tight mb-2">
              You're in.
            </h1>
            <p className="text-sm text-zinc-400 mb-8">
              Welcome to PROX, {status.name}. Here's your private proxy number:
            </p>
            <div
              className="bg-[#0e0e10] border border-[#27272a] p-6 mb-6 cursor-pointer group"
              onClick={copyNumber}
              data-testid="proxy-number-display"
            >
              <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-2">
                Your PROX Number
              </p>
              <p className="font-mono text-3xl text-emerald-400 tracking-wide">
                {status.proxy_number}
              </p>
              <p className="font-mono text-[10px] text-zinc-600 mt-2 flex items-center justify-center gap-1 group-hover:text-zinc-400 transition-colors">
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Click to copy</>}
              </p>
            </div>
            <div className="text-left bg-[#0e0e10] border border-[#1f1f23] p-5 mb-6">
              <p className="font-mono text-xs text-zinc-500 uppercase mb-3">What's next</p>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>1. Share your PROX number instead of your real one</li>
                <li>2. Incoming texts appear as separate threads on your phone</li>
                <li>3. Reply <code className="text-emerald-400 bg-[#0b0b0d] px-1">HELP</code> to any shadow thread for commands</li>
              </ul>
            </div>
            <Link
              to="/"
              className="font-mono text-xs text-zinc-500 hover:text-white transition-colors"
            >
              Back to prox.day
            </Link>
          </div>
        ) : (
          <div data-testid="signup-error">
            <h1 className="font-heading text-2xl tracking-tight mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-zinc-400 mb-6">
              We couldn't complete your signup. Please try again or contact support@prox.day.
            </p>
            <Link
              to="/signup"
              className="inline-block bg-white text-black font-mono text-sm px-6 py-3 hover:bg-zinc-200 transition-colors"
            >
              Try Again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
